import React, { useEffect, useRef } from 'react';
import {
  Card, Divider, Grid, Stack, Typography, Box, useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie,
} from 'recharts';

import BottomBar from 'features/navigation/bottomBar';
import ExportImage from 'common/components/exportImage';
import store from 'app/store';
import * as Constants from 'common/Constants';
import renderActiveShape from './common/renderActiveShape';
import { getDemographicById } from './state/service';

function CustomTooltip({ active, payload, label }) {
  const censusPayload = payload?.slice(0, 3);
  const benchmarkPayload = payload?.slice(3, 6);
  if (active && payload && payload?.length) {
    return (
      <Box style={{ background: Constants.WHITE, padding: '10px', border: '1px solid black' }}>
        <Box component="span">{label}</Box>
        <Box>
          <Box component="span" style={{ fontWeight: 'bold' }}>Census</Box>
          {censusPayload.map((pld) => (
            <Box key={pld.name} style={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="span"
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: pld?.fill,
                  marginRight: '5px',
                }}
              />
              <Box component="span">
                {`${pld?.name} : ${pld?.value}`}
              </Box>
            </Box>
          ))}
          <Box component="span" style={{ fontWeight: 'bold' }}>Benchmark</Box>
          {benchmarkPayload.map((pld) => (
            <Box key={pld.name} style={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="span"
                style={{
                  width: '10px',
                  height: '10px',
                  backgroundColor: pld?.fill,
                  marginRight: '5px',
                }}
              />
              <Box component="span">
                {`${pld?.name} : ${pld?.value}`}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
  return null;
}

function normalize(data) {
  const totalBenchmark = data.reduce((sum, item) => sum + item.percentageOfBenchmark, 0);
  const totalCensus = data.reduce((sum, item) => sum + item.percentageOfCensus, 0);
  if (totalBenchmark === 100 && totalCensus === 100) {
    return data;
  }
  let sumBenchmark = 0;
  let sumCensus = 0;

  const normalizedData = data.map((item, index, array) => {
    const normalizedBenchmark = totalBenchmark !== 0
      ? (item.percentageOfBenchmark / totalBenchmark) * 100 : 0;

    const normalizedCensus = totalCensus !== 0 ? (item.percentageOfCensus / totalCensus) * 100 : 0;

    if (index === array.length - 1) {
      return {
        ...item,
        percentageOfBenchmark: parseFloat((100 - sumBenchmark).toFixed(2)),
        percentageOfCensus: parseFloat((100 - sumCensus).toFixed(2)),
      };
    }

    sumBenchmark += parseFloat(normalizedBenchmark.toFixed(2));
    sumCensus += parseFloat(normalizedCensus.toFixed(2));

    return {
      ...item,
      percentageOfBenchmark: parseFloat(normalizedBenchmark.toFixed(2)),
      percentageOfCensus: parseFloat(normalizedCensus.toFixed(2)),
    };
  });
  return normalizedData;
}

export default function Demograhpic(props) {
  const { bottomBarFunctions } = props;
  const ageDistributionRef = useRef();
  const genderDistributionRef = useRef();
  const relationshipTiersRef = useRef();

  const { handleCancelCreation, goToNextPage, goToPreviousPage } = bottomBarFunctions;
  const demographicFetchFlag = useSelector((state) => state.scenario.demographicFetchFlag);
  const scenarioId = useSelector((state) => state.scenario.scenarioId);
  const ageDistribution = useSelector((state) => state.scenario.ageDistribution);
  const genderDistribution = useSelector((state) => state.scenario.genderDistribution);
  const relationshipDistribution = useSelector((state) => state.scenario.relationshipDistribution);
  const employeeOnly = useSelector((state) => state.scenario.scenario.employeeOnly);
  const { palette } = useTheme();

  const normalizedGenderDistribution = normalize(genderDistribution);
  const normalizedRelationshipDistribution = normalize(relationshipDistribution.filter((item) => item.name !== 'OTHER'));
  const relationShipIndex = Array.isArray(relationshipDistribution)
    ? [...Array(relationshipDistribution.length).keys()]
    : [];
  // eslint-disable-next-line max-len
  const genderIndex = genderDistribution && Array.isArray(genderDistribution) ? [...Array(genderDistribution.length).keys()] : [];

  const RELATION_COLORS = {
    EMPLOYEE: { COLOR: palette.charts.censusMale, POS: 1 },
    CHILD: { COLOR: palette.charts.censusFemale, POS: 2 },
    SPOUSE: { COLOR: palette.charts.spouseRelationship, POS: 3 },
  };

  const GENDER_COLORS = {
    MALE: { COLOR: palette.charts.censusMale, POS: 1 },
    FEMALE: { COLOR: palette.charts.censusFemale, POS: 2 },
    OTHERS: { COLOR: palette.charts.spouseRelationship, POS: 3 },
  };

  useEffect(() => {
    if (scenarioId && scenarioId > 0) {
      store.dispatch(getDemographicById(scenarioId));
    } else {
      // store.dispatch(resetDemographic());
    }
  }, [demographicFetchFlag, scenarioId]);

  return (
    <Stack>
      <Grid container spacing={2} padding={3}>
        <Grid item xs={12}>
          <Typography variant="h4">
            Demographics
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1">
            Analyses of the provided census and what it means
            for your population&apos;s insurance risk.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Card ref={ageDistributionRef}>
            <Grid container spacing={2}>
              <Grid item xs={12} container variant="addPaddingBottom" justifyContent="space-between">
                <Typography variant="h5">
                  Age Distribution
                </Typography>
                <ExportImage sectionRef={ageDistributionRef} sectionName="age_distribution" />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" data-html2canvas-ignore>
                  Compare your population&apos;s age distribution to the Claritev Benchmark.
                  A mature population usually means higher claim incidence
                  rates in key coverages, such as cancer critical illness.
                </Typography>
                <Grid container>
                  <Grid item>
                    <Typography variant="h6" color="black" marginLeft={42}>Census</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="h6" color="black" marginLeft={24}>Benchmark</Typography>
                  </Grid>
                </Grid>
                <ResponsiveContainer width="95%" height={300}>
                  <BarChart
                    data={ageDistribution}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ageBand" />
                    <YAxis name="Percentage" />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      verticalAlign="top"
                      formatter={(value, _, index) => (
                        <span style={{
                          color: 'black',
                          marginRight: index === 2 ? '30px' : 0,
                        }}
                        >
                          {value}
                        </span>
                      )}
                    />
                    <Bar dataKey="malePercentageOfCensus" stackId="census" fill={palette.charts.censusMale} name="Male" />
                    <Bar dataKey="femalePercentageOfCensus" stackId="census" fill={palette.charts.censusFemale} name="Female" />
                    <Bar
                      dataKey="otherPercentageOfCensus"
                      stackId="census"
                      fill={palette.charts.censusOther}
                      name="Other"
                      stroke={palette.charts.censusOther}
                    />
                    <Bar dataKey="malePercentageOfBenchmark" stackId="benchmark" fill={palette.charts.benchmarkMale} name="Male" />
                    <Bar dataKey="femalePercentageOfBenchmark" stackId="benchmark" fill={palette.charts.benchmarkFemale} name="Female" />
                    <Bar
                      dataKey="otherPercentageOfBenchmark"
                      stackId="benchmark"
                      fill={palette.charts.benchmarkOther}
                      name="Other"
                      stroke={palette.charts.benchmarkOther}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card ref={genderDistributionRef}>
            <Grid container spacing={2}>
              <Grid item xs={12} container variant="addPaddingBottom" justifyContent="space-between">
                <Typography variant="h5">
                  Gender Distribution
                </Typography>
                <ExportImage sectionRef={genderDistributionRef} sectionName="gender_distribution" />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" data-html2canvas-ignore>
                  Compare your population&apos;s gender distribution to the Claritev Benchmark,
                  which has been adjusted for age, industry and more.
                  Populations with different gender distributions usually
                  show different key critical illnesses.
                </Typography>
                <ResponsiveContainer width={800} height={500}>
                  <PieChart width={400} height={400}>
                    <Pie
                      activeIndex={genderIndex}
                      activeShape={(e) => renderActiveShape({
                        ...e, category: 'name', noText: false, COLORS: GENDER_COLORS,
                      })}
                      data={normalizedGenderDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={110}
                      outerRadius={130}
                      dataKey="percentageOfCensus"
                      fill="#8884d8"
                    />
                    <Pie
                      activeIndex={genderIndex}
                      activeShape={(e) => renderActiveShape({
                        ...e, category: 'name', noText: true, COLORS: GENDER_COLORS,
                      })}
                      data={normalizedGenderDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="percentageOfBenchmark"
                      fill="#8884d8"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12}>
          {!employeeOnly && (
            <Card ref={relationshipTiersRef}>
              <Grid container spacing={2}>
                <Grid item xs={12} container variant="addPaddingBottom" justifyContent="space-between">
                  <Typography variant="h5">
                    Relationship Tiers
                  </Typography>
                  <ExportImage sectionRef={relationshipTiersRef} sectionName="relationship_tiers" />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" data-html2canvas-ignore>
                    Compare your population&apos;s relationship distribution to the Claritev
                    Benchmark.
                    Spouses and children have different claim risks and rates.
                  </Typography>
                  <ResponsiveContainer width={800} height={500}>
                    <PieChart width={400} height={400}>
                      <Pie
                        activeIndex={relationShipIndex}
                        activeShape={(e) => renderActiveShape({
                          ...e, category: 'name', noText: false, COLORS: RELATION_COLORS,
                        })}
                        data={normalizedRelationshipDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={110}
                        outerRadius={130}
                        dataKey="percentageOfCensus"
                        fill="#8884d8"
                      />
                      <Pie
                        activeIndex={relationShipIndex}
                        activeShape={(e) => renderActiveShape({
                          ...e, category: 'name', noText: true, COLORS: RELATION_COLORS,
                        })}
                        data={normalizedRelationshipDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        dataKey="percentageOfBenchmark"
                        fill="#8884d8"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Grid>
              </Grid>
            </Card>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <BottomBar
          firstButtonDisplayName="EXIT"
          secondButtonDisplayName="GO TO POLICIES"
          onClickFirstButton={handleCancelCreation}
          onClickSecondButton={goToNextPage}
          isBackButton
          onClickBackButton={goToPreviousPage}
        />
      </Grid>
    </Stack>
  );
}
